import { useEffect, useRef, useState } from 'react';
import { useAppSettings } from '../../contexts/SettingsContext';
import { UI_CONSTANTS } from '@petmas/shared';

// Declare Google Identity Services types
declare global {
  interface Window {
    google?: {
      accounts: {
        id: {
          initialize: (config: GoogleInitConfig) => void;
          renderButton: (element: HTMLElement, config: GoogleButtonConfig) => void;
          prompt: () => void;
        };
      };
    };
  }
}

interface GoogleInitConfig {
  client_id: string;
  callback: (response: GoogleCredentialResponse) => void;
  auto_select?: boolean;
}

interface GoogleButtonConfig {
  type?: 'standard' | 'icon';
  theme?: 'outline' | 'filled_blue' | 'filled_black';
  size?: 'large' | 'medium' | 'small';
  text?: 'signin_with' | 'signup_with' | 'continue_with' | 'signin';
  shape?: 'rectangular' | 'pill' | 'circle' | 'square';
  logo_alignment?: 'left' | 'center';
  width?: number;
  locale?: string;
}

interface GoogleCredentialResponse {
  credential: string;
  select_by: string;
}

interface GoogleSignInButtonProps {
  onSuccess: (idToken: string) => void;
  onError?: (error: string) => void;
  text?: 'signin_with' | 'signup_with' | 'continue_with' | 'signin';
}

export function GoogleSignInButton({
  onSuccess,
  onError,
  text = 'continue_with',
}: GoogleSignInButtonProps) {
  const { settings, isLoading: settingsLoading } = useAppSettings();
  const buttonRef = useRef<HTMLDivElement>(null);
  const [isScriptLoaded, setIsScriptLoaded] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Load the Google Identity Services script
  useEffect(() => {
    if (typeof window === 'undefined') return;

    // Check if script is already loaded
    if (window.google?.accounts) {
      setIsScriptLoaded(true);
      return;
    }

    // Check if script tag already exists
    const existingScript = document.querySelector('script[src*="accounts.google.com/gsi/client"]');
    if (existingScript) {
      existingScript.addEventListener('load', () => setIsScriptLoaded(true));
      return;
    }

    // Load the script
    const script = document.createElement('script');
    script.src = 'https://accounts.google.com/gsi/client';
    script.async = true;
    script.defer = true;
    script.onload = () => setIsScriptLoaded(true);
    script.onerror = () => {
      setError('Failed to load Google Sign-In');
      onError?.('Failed to load Google Sign-In script');
    };
    document.head.appendChild(script);

    return () => {
      // Don't remove the script on unmount as other components might use it
    };
  }, [onError]);

  // Initialize Google Sign-In when script is loaded and we have settings
  useEffect(() => {
    if (!isScriptLoaded || settingsLoading || !buttonRef.current) return;

    const clientId = settings?.features?.googleClientId;
    if (!clientId || !settings?.features?.googleSigninEnabled) {
      return;
    }

    try {
      window.google?.accounts.id.initialize({
        client_id: clientId,
        callback: (response: GoogleCredentialResponse) => {
          if (response.credential) {
            onSuccess(response.credential);
          } else {
            setError('No credential received from Google');
            onError?.('No credential received from Google');
          }
        },
      });

      // Render the button
      window.google?.accounts.id.renderButton(buttonRef.current, {
        type: 'standard',
        theme: 'outline',
        size: 'large',
        text,
        shape: 'rectangular',
        logo_alignment: 'left',
        width: buttonRef.current.offsetWidth || UI_CONSTANTS.DEFAULT_BUTTON_WIDTH,
        locale: 'es',
      });
    } catch (err) {
      console.error('Error initializing Google Sign-In:', err);
      setError('Failed to initialize Google Sign-In');
      onError?.('Failed to initialize Google Sign-In');
    }
  }, [isScriptLoaded, settingsLoading, settings, onSuccess, onError, text]);

  // Don't show if Google Sign-In is not enabled
  if (settingsLoading) {
    return (
      <div className="h-10 bg-gray-100 rounded animate-pulse" />
    );
  }

  if (!settings?.features?.googleSigninEnabled || !settings?.features?.googleClientId) {
    return null;
  }

  if (error) {
    return (
      <div className="text-sm text-red-600 text-center py-2">
        {error}
      </div>
    );
  }

  return (
    <div ref={buttonRef} className="flex justify-center" />
  );
}
